import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/portfolio/Navbar";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Share2,
  Check,
  Sparkles,
  ExternalLink,
  ChevronRight,
  CheckSquare,
  Square,
  Zap,
  Bot,
  Search,
  Layers,
  ArrowUpRight,
  HelpCircle,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Target,
  BarChart3,
  Compass,
  Table,
  MessageSquare,
  Sparkle
} from "lucide-react";

export default function KeywordResearchAiSearchPostPage() {
  const [copied, setCopied] = useState(false);
  const [checkedSteps, setCheckedSteps] = useState({});

  const toggleStep = (id) => {
    setCheckedSteps((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const originalTitle = document.title;
    const pageTitle = "How to Do Keyword Research for AI Visibility: A Customer-Intent-First Approach | Kanna Narendra Kumar";
    const pageDesc = "Looking only at search volume in tools like Semrush or Ahrefs is not enough for AI visibility. Learn a simple 11-step customer-intent-first approach using Google Search Console, Reddit, customer reviews, and Question Memory to get cited in ChatGPT, Claude, and Google AI Overviews.";
    const pageUrl = "https://narendradigital.in/blog/how-to-do-keyword-research-for-ai-visibility/";

    document.title = pageTitle;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attr, value);
      } else {
        const meta = document.createElement("meta");
        const isProp = selector.includes("property=");
        if (isProp) {
          const match = selector.match(/property="([^"]+)"/);
          if (match) meta.setAttribute("property", match[1]);
        } else {
          const match = selector.match(/name="([^"]+)"/);
          if (match) meta.setAttribute("name", match[1]);
        }
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
      }
    };

    setMeta('meta[name="description"]', "content", pageDesc);
    setMeta('meta[property="og:title"]', "content", pageTitle);
    setMeta('meta[property="og:description"]', "content", pageDesc);
    setMeta('meta[property="og:url"]', "content", pageUrl);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[property="og:image"]', "content", "https://narendradigital.in/og-image-v3.jpg");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", pageTitle);
    setMeta('meta[name="twitter:description"]', "content", pageDesc);
    setMeta('meta[name="twitter:image"]', "content", "https://narendradigital.in/og-image-v3.jpg");

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    const originalCanonical = canonical ? canonical.getAttribute("href") : null;
    canonical.setAttribute("href", pageUrl);

    // Inject JSON-LD Schema: TechArticle + BreadcrumbList + FAQPage
    const scriptId = "keyword-research-ai-visibility-schema";
    let scriptTag = document.getElementById(scriptId);
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = scriptId;
      scriptTag.type = "application/ld+json";
      scriptTag.text = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "TechArticle",
            "@id": `${pageUrl}#article`,
            "isPartOf": {
              "@type": "WebSite",
              "@id": "https://narendradigital.in/#website",
              "name": "Kanna Narendra Kumar — Senior SEO & AI Search Strategist",
              "url": "https://narendradigital.in"
            },
            "headline": "How to Do Keyword Research for AI Visibility: A Customer-Intent-First Approach",
            "description": pageDesc,
            "image": "https://narendradigital.in/og-image-v3.jpg",
            "datePublished": "2026-11-02T10:00:00+05:30",
            "dateModified": "2026-11-02T12:00:00+05:30",
            "mainEntityOfPage": pageUrl,
            "author": {
              "@type": "Person",
              "@id": "https://narendradigital.in/#author",
              "name": "Kanna Narendra Kumar",
              "jobTitle": "Senior SEO Specialist & AI Search Strategist",
              "url": "https://narendradigital.in/",
              "sameAs": [
                "https://www.linkedin.com/in/narendra-kumar-kanna/"
              ]
            },
            "publisher": {
              "@type": "Person",
              "name": "Kanna Narendra Kumar"
            },
            "keywords": [
              "Keyword Research for AI Visibility",
              "AI Visibility Intent Keywords",
              "Customer Intent Keyword Research",
              "Question Memory Framework",
              "ChatGPT Visibility Optimization",
              "AEO GEO Keyword Strategy",
              "Google Search Console Intent Research",
              "Generative Engine Optimization",
              "Hybrid Engine Optimization HEO"
            ],
            "articleSection": "AEO/GEO , AI Visibility",
            "inLanguage": "en-US"
          },
          {
            "@type": "BreadcrumbList",
            "@id": `${pageUrl}#breadcrumb`,
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://narendradigital.in/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://narendradigital.in/blog/"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "How to Do Keyword Research for AI Visibility",
                "item": pageUrl
              }
            ]
          },
          {
            "@type": "FAQPage",
            "@id": `${pageUrl}#faq`,
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Why is traditional keyword volume alone not enough for AI visibility?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Traditional SEO tools show you monthly search volume for short phrases. But for AI visibility (like in ChatGPT, Claude, and Perplexity), people ask conversational questions, explain their problems, compare software, and look for specific recommendations. Knowing the volume doesn't tell you what makes a customer choose or reject a product."
                }
              },
              {
                "@type": "Question",
                "name": "What is a Question Memory?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "A Question Memory is a central document or spreadsheet where you collect the exact questions, objections, and buying criteria real customers ask across Google Search Console, Reddit, Quora, product reviews, sales calls, support tickets, and community comments."
                }
              },
              {
                "@type": "Question",
                "name": "How do you group questions by customer intent?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Questions can be organized into 4 clear stages of customer intent: 1. Problem awareness (identifying an issue), 2. Solution research (learning possible fixes), 3. Product comparison (weighing options), and 4. Purchase decision (final checks on pricing, migration, and implementation)."
                }
              },
              {
                "@type": "Question",
                "name": "Does Question Memory replace tools like Semrush and Ahrefs?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No, it does not replace them. SEO tools are very helpful for checking search volume, competition, and ranking trends. Question Memory works together with them by adding real customer context that tools cannot give you on their own."
                }
              }
            ]
          }
        ]
      });
      document.head.appendChild(scriptTag);
    }

    window.scrollTo(0, 0);

    return () => {
      document.title = originalTitle;
      if (canonical) {
        if (createdCanonical) {
          canonical.remove();
        } else if (originalCanonical) {
          canonical.setAttribute("href", originalCanonical);
        }
      }
      if (scriptTag) scriptTag.remove();
    };
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const checklistItems = [
    { id: "gsc", title: "1. Google Search Console", desc: "Filter by product keywords and pick queries with true business value." },
    { id: "reddit", title: "2. Reddit & Quora", desc: "Find discussions comparing products, budgets, and user problems." },
    { id: "reviews", title: "3. Customer & Competitor Reviews", desc: "Read G2, Capterra, and Trustpilot reviews to find product gaps to solve." },
    { id: "sales", title: "4. Sales Calls & Support Tickets", desc: "Collect repeated objections and implementation doubts from real buyers." },
    { id: "suggest", title: "5. Google Autocomplete & PAA", desc: "Note natural phrasing and related question snippets in search results." },
    { id: "forums", title: "6. Industry Forums", desc: "Discover specific community questions that keyword tools miss." },
    { id: "youtube", title: "7. YouTube Comments", desc: "Extract unanswered questions from popular tutorial and comparison videos." },
    { id: "social", title: "8. Influencers & Social Posts", desc: "Review user comments for misconceptions, budgets, and buying preferences." },
    { id: "grouping", title: "9. Intent Grouping", desc: "Organize collected questions into problem, solution, comparison, and purchase intent." },
    { id: "memory", title: "10. Question Memory Hub", desc: "Maintain the questions in your central spreadsheet for ongoing content planning." },
    { id: "dual", title: "11. Traditional SEO + AI Visibility", desc: "Answer the full problem directly with clear explanations, facts, and prompt testing." }
  ];

  const completedCount = Object.values(checkedSteps).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#070a12] text-white selection:bg-cyan-500 selection:text-black">
      {/* Background Glows */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-indigo-600/10 rounded-full blur-[180px] pointer-events-none" />
      <div className="fixed top-96 right-10 w-[500px] h-[350px] bg-cyan-600/10 rounded-full blur-[160px] pointer-events-none" />

      {/* Main Global Navigation Bar */}
      <Navbar />

      <main className="max-w-4xl lg:max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14 relative z-10">
        {/* Article Breadcrumbs & Quick Utility Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <nav className="flex items-center gap-2 text-xs font-mono text-white/50">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3 text-white/30" />
            <Link to="/blog/" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="w-3 h-3 text-white/30" />
            <span className="text-cyan-400 truncate">AEO / GEO &amp; AI Visibility</span>
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/blog/"
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-white/70 hover:text-cyan-400 px-3 py-1.5 rounded-full border border-white/10 hover:border-cyan-500/30 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Blog</span>
            </Link>
            <button
              onClick={handleCopyLink}
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-cyan-400 hover:text-white px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 transition-all cursor-pointer"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5" />}
              <span>{copied ? "Copied" : "Share"}</span>
            </button>
          </div>
        </div>

        {/* Article Header */}
        <header className="space-y-6 pb-8 border-b border-white/10">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 uppercase tracking-wider">
              AEO/GEO , AI Visibility
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 uppercase tracking-wider">
              Keyword Research for AI Visibility
            </span>
          </div>

          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.15]">
            How to Do Keyword Research for AI Visibility: A Customer-Intent-First Approach
          </h1>

          <p className="text-base sm:text-lg text-white/70 leading-relaxed font-normal">
            Traditional keyword research shows what people type into search boxes. But does it tell you what customers are struggling with, what they compare before buying, or what stops them from choosing a product? Here is a practical, intent-first approach to rank in Google and gain citations in AI answers.
          </p>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 text-xs font-mono text-white/50">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-white/80 font-bold">Kanna Narendra Kumar</span>
              <span>&bull;</span>
              <span className="text-cyan-400">Senior SEO Specialist &amp; AI Search Strategist</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                <span>Nov 02, 2026</span>
              </span>
              <span>&bull;</span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-indigo-400" />
                <span>10 min read</span>
              </span>
            </div>
          </div>
        </header>

        {/* Key Takeaways Box — Friendly Comparison & Clear Value */}
        <section
          aria-label="Key Takeaways"
          className="my-7 p-5 sm:p-6 rounded-xl bg-[#0c1626] border-2 border-cyan-400 shadow-lg shadow-cyan-950/20"
        >
          <div className="flex items-center gap-2 mb-3.5 text-cyan-400">
            <Sparkles className="w-4 h-4 shrink-0 text-cyan-400" />
            <h2 className="text-xs sm:text-sm font-mono font-bold uppercase tracking-wider text-cyan-400 m-0">
              Key Takeaways
            </h2>
          </div>

          <ul className="list-disc pl-5 space-y-2.5 text-sm sm:text-[15px] text-slate-200 leading-relaxed marker:text-cyan-400">
            <li>
              <strong className="text-white">Search volume alone is not enough:</strong> Popular SEO tools like Semrush and Ahrefs are very helpful for finding search volume and broad keywords. But search volume alone does not tell you what problems customers struggle with, what makes them hesitate, or what they compare before buying.
            </li>
            <li>
              <strong className="text-white">AI search relies on real problems:</strong> People using ChatGPT, Claude, and Google AI Overviews ask complete questions, explain their situations, and compare options based on exact needs.
            </li>
            <li>
              <strong className="text-white">Start with your own Search Console data:</strong> Look at the queries people already use to discover your site. Pick the ones that have real business value and high customer intent.
            </li>
            <li>
              <strong className="text-white">Build a &quot;Question Memory&quot;:</strong> Collect real questions from Reddit, Quora, product reviews, sales demos, and support tickets in a central spreadsheet. This becomes your secret weapon for content ideas.
            </li>
            <li>
              <strong className="text-white">Group questions by buying stages:</strong> Sort your questions into 4 simple buckets: Problem awareness, Solution research, Product comparison, and Purchase decision.
            </li>
            <li>
              <strong className="text-white">The best strategy uses both:</strong> Traditional tools show you <em>what</em> people search for. Your Question Memory shows you <em>how to solve their problems</em>. Combining both gives you the best visibility across Google and AI search engines.
            </li>
          </ul>
        </section>

        {/* Visual Workflow: From Customer Need to AI Citation */}
        <div className="my-8 p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-[#0c1427] to-[#09101d] border border-cyan-500/20">
          <div className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-wider mb-2 flex items-center gap-2">
            <Compass className="w-4 h-4" />
            <span>The Customer-Intent AI Keyword Research Loop</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 pt-2 text-center text-xs">
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-cyan-400 font-bold block mb-1">01. GSC Seeds</span>
              <p className="text-[11px] text-white/60">Extract high-relevance queries already bringing visitors</p>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-indigo-400 font-bold block mb-1">02. Community Mining</span>
              <p className="text-[11px] text-white/60">Find real questions on Reddit, Quora &amp; Reviews</p>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-purple-400 font-bold block mb-1">03. Question Memory</span>
              <p className="text-[11px] text-white/60">Store unfiltered questions in a central spreadsheet</p>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-amber-400 font-bold block mb-1">04. Intent Grouping</span>
              <p className="text-[11px] text-white/60">Group questions into 4 stages of buying journey</p>
            </div>
            <div className="p-3 rounded-xl bg-cyan-950/40 border border-cyan-500/40">
              <span className="font-mono text-emerald-300 font-bold block mb-1">05. Dual Ranking</span>
              <p className="text-[11px] text-white/80">Rank on Google &amp; get cited in AI answers</p>
            </div>
          </div>
        </div>

        {/* Article Body */}
        <article className="py-6 space-y-8 text-white/85 text-base sm:text-lg leading-relaxed font-sans">
          
          <div className="space-y-4">
            <p>
              Traditional keyword research tells you what people search for. But does it tell you what customers are struggling with, what they compare before buying, or what stops them from choosing a product?
            </p>
            <p>
              Not always.
            </p>
            <p>
              With AI search, people ask detailed questions, explain their problems and compare solutions based on their specific requirements. Finding these questions requires more than looking at search volume in SEO tools.
            </p>
            <blockquote className="p-5 sm:p-6 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-bold text-lg sm:text-xl shadow-xl my-6 not-italic">
              You need to understand what your customers are actually asking.
            </blockquote>
            <p>
              In this guide, I will explain a practical approach to keyword research that combines Google Search Console, customer discussions, product reviews, sales conversations and community questions to find valuable content opportunities for both traditional search and AI search.
            </p>
            <p>
              As covered in my breakdown on{" "}
              <Link
                to="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/"
                className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
              >
                why websites rank on Google but don&apos;t appear in ChatGPT
              </Link>
              , AI answer engines use fan-out queries to evaluate complete customer decisions rather than single keywords.
            </p>
          </div>

          {/* TABLE 1: Comprehensive Comparison Matrix */}
          <div className="my-8">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-cyan-400" />
              <h3 className="font-heading font-bold text-base sm:text-lg text-white">
                Table 1: Traditional Keyword Research vs. AI Intent Research
              </h3>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#090f1e]">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.04]">
                    <th className="p-3.5 font-mono text-cyan-300 font-bold uppercase">Attribute</th>
                    <th className="p-3.5 font-mono text-white/80 font-bold uppercase">Traditional Keyword Research</th>
                    <th className="p-3.5 font-mono text-emerald-300 font-bold uppercase">Customer-Intent AI Research</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06] text-white/80">
                  <tr>
                    <td className="p-3.5 font-mono text-cyan-400 font-semibold">Primary Metric</td>
                    <td className="p-3.5">Monthly Search Volume (MSV) &amp; Keyword Difficulty (KD)</td>
                    <td className="p-3.5 text-emerald-200">Customer Intent, Decision Blocks &amp; Buying Triggers</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-mono text-cyan-400 font-semibold">Search Format</td>
                    <td className="p-3.5">Short phrases (e.g. &quot;HRMS software&quot;, &quot;best CRM&quot;)</td>
                    <td className="p-3.5 text-emerald-200">Conversational questions &amp; multi-condition prompts</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-mono text-cyan-400 font-semibold">Data Source</td>
                    <td className="p-3.5">Third-party database scrapers &amp; Google Ads clickstream</td>
                    <td className="p-3.5 text-emerald-200">GSC queries, Reddit, customer reviews, sales calls &amp; support</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-mono text-cyan-400 font-semibold">What It Misses</td>
                    <td className="p-3.5">Why customers hesitate, specific objections, and budget realities</td>
                    <td className="p-3.5 text-emerald-200">Historical broad search volume trends</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-mono text-cyan-400 font-semibold">How AI Uses It</td>
                    <td className="p-3.5">Matches broad category entities</td>
                    <td className="p-3.5 text-emerald-200">Retrieves exact answers to fan-out queries and cites solutions</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-mono text-cyan-400 font-semibold">Best Purpose</td>
                    <td className="p-3.5">Market sizing &amp; top-of-funnel broad traffic planning</td>
                    <td className="p-3.5 text-emerald-200">High-converting revenue content, comparison pages &amp; AI answers</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <hr className="border-white/10 my-8" />

          {/* Section 1 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              1. Start With Your Existing Google Search Console Data
            </h2>
            <p>
              Before looking for new keywords, understand what people are already searching for to find your business.
            </p>
            <p>
              Open Google Search Console and go to the <strong>Performance report</strong>.
            </p>
            <p>
              Filter queries using your main product or service keyword.
            </p>
            <div className="p-4 rounded-xl bg-[#0b1325] border border-white/10 space-y-2 text-sm text-white/80">
              <p className="font-bold text-cyan-300">Quick examples:</p>
              <ul className="list-disc pl-5 space-y-1 text-white/70">
                <li><strong>HRMS software:</strong> Filter queries containing &quot;HRMS&quot;.</li>
                <li><strong>Web development:</strong> Filter queries containing &quot;web&quot;.</li>
                <li><strong>CRM software:</strong> Filter queries containing &quot;CRM&quot;.</li>
                <li><strong>Expense management:</strong> Filter queries containing &quot;expense&quot;.</li>
              </ul>
            </div>
            <p>
              You can use custom regular expressions (regex) to collect different keyword variations into one report and export the results into a spreadsheet.
            </p>
            <p>
              Now, don&apos;t treat every query as a keyword you need to target.
            </p>
            <p>
              Handpick the queries that have <strong>genuine business relevance</strong>. Look at their impressions, clicks and search intent.
            </p>
            <p>
              These queries are your starting point. They show how people already discover your business through Google. If you want to verify whether these URLs are accessible to modern search crawlers, check our{" "}
              <Link
                to="/how-to-audit-a-website-for-ai-visibility/"
                className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
              >
                10-point website audit for AI visibility
              </Link>
              .
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Section 2 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              2. Find Real Customer Questions on Reddit and Quora
            </h2>
            <p>
              Take each important keyword from Google Search Console and search for discussions around it on Reddit and Quora.
            </p>
            <p>
              Don&apos;t just look for questions containing your exact keyword.
            </p>
            <p>Look for people who are:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-white/80">
              <li>Comparing different products.</li>
              <li>Looking for recommendations.</li>
              <li>Discussing pricing and budgets.</li>
              <li>Considering switching from one product to another.</li>
              <li>Experiencing problems with existing solutions.</li>
              <li>Trying to understand which product suits their requirements.</li>
            </ul>
            <div className="p-5 rounded-2xl bg-[#091122] border border-cyan-500/20 space-y-2">
              <span className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-wider">Example Real Questions</span>
              <p className="text-sm text-white/90">If your keyword is <em>HRMS software</em>, look for questions such as:</p>
              <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-slate-300">
                <li>&quot;Which HRMS software is suitable for a small business?&quot;</li>
                <li>&quot;What should I check before switching HRMS platforms?&quot;</li>
                <li>&quot;Is an HRMS worth the cost for a company with 50 employees?&quot;</li>
                <li>&quot;Which HRMS software supports Indian payroll compliance?&quot;</li>
              </ul>
            </div>
            <p>
              You can also use tools like ChatGPT or Claude to help you find and organize these discussions quickly.
            </p>
            <p className="font-semibold text-white">
              Keep all the original questions in a separate spreadsheet. I call this your <span className="text-cyan-400">Question Memory</span>.
            </p>
            <p>
              This is one of the most valuable inputs for your keyword and prompt research.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Section 3 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              3. Study Customer and Competitor Reviews
            </h2>
            <p>
              Your customers often explain what they like, dislike and expect from a product through reviews.
            </p>
            <p>
              Explore platforms such as:
            </p>
            <div className="flex flex-wrap gap-2 text-xs font-mono text-cyan-300">
              <span className="px-3 py-1 rounded-lg bg-cyan-950/60 border border-cyan-500/30">Google Reviews</span>
              <span className="px-3 py-1 rounded-lg bg-cyan-950/60 border border-cyan-500/30">G2</span>
              <span className="px-3 py-1 rounded-lg bg-cyan-950/60 border border-cyan-500/30">Capterra</span>
              <span className="px-3 py-1 rounded-lg bg-cyan-950/60 border border-cyan-500/30">Trustpilot</span>
              <span className="px-3 py-1 rounded-lg bg-cyan-950/60 border border-cyan-500/30">Industry review sites</span>
            </div>
            <p>
              Pay particular attention to negative reviews. The goal here is <strong>never to downgrade or criticize competitors</strong>, but to understand what real users struggle with so you can help them better.
            </p>
            <p>Look for:</p>
            <ul className="list-disc pl-5 space-y-1 text-white/80">
              <li>Missing features that users wish existed.</li>
              <li>Difficult setup or long implementation time.</li>
              <li>Slow or unhelpful customer support.</li>
              <li>Confusing pricing plans and hidden costs.</li>
              <li>Integration problems with other tools.</li>
              <li>Product limitations for growing teams.</li>
            </ul>
            <p>
              Do the same research for competitors in your category.
            </p>
            <p>
              Their reviews can reveal common customer pain points that your product may be able to solve easily.
            </p>
            <p>
              For example, in my work with B2B SaaS platforms (such as our{" "}
              <Link
                to="/casestudy/myoperator/"
                className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
              >
                MyOperator growth case study
              </Link>
              ), analyzing customer review pain points revealed high-intent topics that generated over 60% of all inbound demo bookings.
            </p>
            <p>
              Add these questions, problems and product comparisons to your Question Memory.
            </p>
          </section>

          {/* Mid-Article AI Visibility Audit CTA Callout (~30% Scroll) */}
          <div className="my-8 p-6 sm:p-7 rounded-2xl bg-gradient-to-r from-[#0c1629] via-[#0f1d38] to-[#0c1426] border border-cyan-500/35 shadow-xl shadow-cyan-950/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 not-prose">
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold text-cyan-400 uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>AI Search Readiness Check</span>
              </div>
              <h3 className="font-heading font-bold text-lg sm:text-xl text-white leading-snug">
                Want Us to Map Your Customer Fan-Out Questions &amp; AI Visibility Gaps?
              </h3>
              <p className="text-xs sm:text-sm text-white/70 max-w-xl leading-relaxed">
                Get a complete 4-layer AI Visibility Audit covering bot access, zero-JS HTML rendering, and high-intent customer prompt opportunities across ChatGPT, Google AI Overviews, and Claude.
              </p>
            </div>
            <Link
              to="/ai-visibility-audit/"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 hover:scale-[1.02] transition-all"
            >
              <span>Get an AI Visibility Audit</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <hr className="border-white/10 my-8" />

          {/* Section 4 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              4. Collect Questions From Sales Calls, Demos and Support Tickets
            </h2>
            <p>
              Your existing customers and prospects are one of the best sources of keyword and prompt research.
            </p>
            <p>
              Go through your:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-white/80">
              <li>Product demo recordings.</li>
              <li>Sales conversations.</li>
              <li>Webinar recordings.</li>
              <li>Customer support tickets.</li>
              <li>Support emails.</li>
              <li>Onboarding conversations.</li>
            </ul>
            <p>
              You do not have to watch every recording manually.
            </p>
            <p>
              If you have call or webinar transcripts, you can use ChatGPT or Claude to extract the exact questions customers ask.
            </p>
            <p>
              Look for repeated questions, objections, purchasing concerns and specific requirements:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-slate-300 text-sm">
              <li>&quot;Does this software integrate with our existing ERP?&quot;</li>
              <li>&quot;How long does implementation usually take?&quot;</li>
              <li>&quot;Can we migrate our existing employee data safely?&quot;</li>
              <li>&quot;Does the platform support multiple approval levels?&quot;</li>
            </ul>
            <p>
              These questions reveal what customers actually need to know before making a purchase decision. Also ensure that when AI crawlers discover your answers, they can crawl them without barrier, as outlined in our{" "}
              <Link
                to="/blog/can-ai-search-engines-crawl-understand-cite-website/"
                className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
              >
                AI search engine crawling and citation guide
              </Link>
              .
            </p>
            <p>
              Add them to your Question Memory.
            </p>
            <div className="p-4 rounded-xl bg-amber-950/20 border border-amber-500/30 text-amber-200 text-xs sm:text-sm">
              <strong>Tip:</strong> Always remove confidential customer details or company private data before uploading transcripts to AI tools.
            </div>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Section 5 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              5. Explore Google Search Suggestions and Related Questions
            </h2>
            <p>
              Search for each of your important business keywords in Google.
            </p>
            <p>
              Collect relevant suggestions from:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-white/80">
              <li>Google Autocomplete (the dropdown suggestions as you type).</li>
              <li>People Also Ask (PAA) expandable boxes.</li>
              <li>Related searches at the bottom of the results page.</li>
              <li>Relevant search result snippets.</li>
            </ul>
            <p>
              These sources help you discover additional ways people express their requirements in simple everyday words.
            </p>
            <p>
              Don&apos;t simply collect every suggested keyword. Keep only the ones that are relevant to your product, audience, and real customer problems.
            </p>
            <p>
              Add these to your existing spreadsheet.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Section 6 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              6. Find Questions in Industry Forums
            </h2>
            <p>
              Every industry has communities where people discuss problems and look for solutions.
            </p>
            <p>
              Identify forums that are relevant to your business.
            </p>
            <p>
              For example, a SaaS company targeting HR professionals can explore HR communities, payroll forums, LinkedIn groups, and business management discussions.
            </p>
            <p>
              Look for questions about product selection, implementation, pricing, integrations and common problems.
            </p>
            <p>
              These discussions can reveal questions that may not appear in traditional keyword research tools because people ask them naturally in everyday sentences.
            </p>
            <p>
              Add the relevant questions to your Question Memory.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Section 7 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              7. Explore Questions in YouTube Comments
            </h2>
            <p>
              Find popular YouTube videos related to your product, service or industry.
            </p>
            <p>
              Look for videos with meaningful engagement, especially tutorials, product comparisons, reviews and problem-solving guides.
            </p>
            <p>
              Don&apos;t stop at watching the video. <strong>Go through the comments.</strong>
            </p>
            <p>
              People often use the comment section to ask questions that the video did not answer:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-slate-300 text-sm">
              <li>&quot;Does this work for small teams with 5 people?&quot;</li>
              <li>&quot;Is there an affordable option for startups?&quot;</li>
              <li>&quot;Can I set this up without coding knowledge?&quot;</li>
              <li>&quot;Does this support multiple currencies or locations?&quot;</li>
            </ul>
            <p>
              You can use simple AI prompts with ChatGPT or Claude to extract questions from exported comments.
            </p>
            <p>
              Add these questions to your Question Memory.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Section 8 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              8. Explore Influencer Videos and Social Media Discussions
            </h2>
            <p>
              Repeat this process with industry creators, LinkedIn posts, and Instagram Reels or short-form videos.
            </p>
            <p>
              Look for posts discussing your product category, common customer headaches, and competing solutions.
            </p>
            <p>
              Read the comments and identify questions that reveal:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-white/80">
              <li>Customer expectations.</li>
              <li>Product-related doubts.</li>
              <li>Common misconceptions.</li>
              <li>Buying preferences.</li>
              <li>Unresolved problems.</li>
            </ul>
            <p>
              Only collect questions that are relevant to your business. A post being popular does not automatically make a question a valuable content opportunity.
            </p>
            <p>
              Add the findings to your Question Memory.
            </p>
          </section>

          {/* TABLE 2: The 8 Research Channels Comparison Matrix */}
          <div className="my-8">
            <div className="flex items-center gap-2 mb-3">
              <Table className="w-4 h-4 text-cyan-400" />
              <h3 className="font-heading font-bold text-base sm:text-lg text-white">
                Table 2: Where to Find Real Customer Questions (The 8 Channels)
              </h3>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#090f1e]">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.04]">
                    <th className="p-3.5 font-mono text-cyan-300 font-bold uppercase">Source Channel</th>
                    <th className="p-3.5 font-mono text-white/80 font-bold uppercase">What You Will Find</th>
                    <th className="p-3.5 font-mono text-emerald-300 font-bold uppercase">Real Question Example</th>
                    <th className="p-3.5 font-mono text-purple-300 font-bold uppercase">Intent Level</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06] text-white/80">
                  <tr>
                    <td className="p-3.5 font-semibold text-white">1. Search Console</td>
                    <td className="p-3.5">Existing searches discovering your brand</td>
                    <td className="p-3.5 text-cyan-200">&quot;payroll compliance check for remote workers&quot;</td>
                    <td className="p-3.5 text-emerald-400 font-mono text-xs">High / Commercial</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">2. Reddit &amp; Quora</td>
                    <td className="p-3.5">Unfiltered tool comparisons &amp; budget doubts</td>
                    <td className="p-3.5 text-cyan-200">&quot;Is Tool X worth the cost for 20 employees?&quot;</td>
                    <td className="p-3.5 text-emerald-400 font-mono text-xs">High / Evaluation</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">3. Review Platforms</td>
                    <td className="p-3.5">Pain points &amp; competitor missing features</td>
                    <td className="p-3.5 text-cyan-200">&quot;Reporting is too slow and crashes on export&quot;</td>
                    <td className="p-3.5 text-emerald-400 font-mono text-xs">High / Switching Intent</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">4. Sales &amp; Demos</td>
                    <td className="p-3.5">Final objections, migration worries &amp; security</td>
                    <td className="p-3.5 text-cyan-200">&quot;Can we import historical payroll data from Excel?&quot;</td>
                    <td className="p-3.5 text-emerald-400 font-mono text-xs">Highest / Purchase</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">5. Google Suggest / PAA</td>
                    <td className="p-3.5">Natural language phrasing &amp; adjacent topics</td>
                    <td className="p-3.5 text-cyan-200">&quot;What is the penalty for late PF payment in India?&quot;</td>
                    <td className="p-3.5 text-emerald-400 font-mono text-xs">Medium / Problem Aware</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">6. Industry Forums</td>
                    <td className="p-3.5">Specialized compliance &amp; workflow questions</td>
                    <td className="p-3.5 text-cyan-200">&quot;How do people handle multi-state professional tax?&quot;</td>
                    <td className="p-3.5 text-emerald-400 font-mono text-xs">High / Solution Research</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">7. YouTube Comments</td>
                    <td className="p-3.5">Practical doubts left unanswered by tutorials</td>
                    <td className="p-3.5 text-cyan-200">&quot;Do you need developer skills to set this up?&quot;</td>
                    <td className="p-3.5 text-emerald-400 font-mono text-xs">Medium / Usability</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">8. Social Media Posts</td>
                    <td className="p-3.5">Trends, misconceptions &amp; pricing expectations</td>
                    <td className="p-3.5 text-cyan-200">&quot;Are there any free tools for attendance tracking?&quot;</td>
                    <td className="p-3.5 text-emerald-400 font-mono text-xs">Broad / Early Awareness</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <hr className="border-white/10 my-8" />

          {/* Section 9 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              9. Turn Your Question Memory Into Keyword and Prompt Opportunities
            </h2>
            <p>
              Now you have collected questions from multiple sources.
            </p>
            <p>
              But collecting questions is not the final objective.
            </p>
            <p>
              The real work starts when you identify the common problems and decisions behind them.
            </p>
            <p>
              <strong>Group similar questions into topics.</strong>
            </p>
            <p>
              For example, suppose you sell HRMS software and have collected these questions:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-slate-300 text-sm">
              <li>Which HRMS software is suitable for a 50-person company?</li>
              <li>What is the best HRMS for small businesses?</li>
              <li>Is HRMS software affordable for startups?</li>
              <li>Which HRMS supports Indian payroll?</li>
              <li>How difficult is it to migrate employee data?</li>
            </ul>
            <p>
              You can group them into 4 clear topics:
            </p>
            <ol className="list-decimal pl-5 space-y-1 text-cyan-300 text-sm font-semibold">
              <li>HRMS software for small businesses.</li>
              <li>HRMS pricing and affordability.</li>
              <li>HRMS payroll capabilities and compliance.</li>
              <li>HRMS implementation and data migration.</li>
            </ol>
            <p>
              This forms the heart of what I call the{" "}
              <a
                href="/#heo"
                className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
              >
                Hybrid Engine Optimization (HEO) framework
              </a>
              , which aligns your content for both traditional SEO algorithms and LLM answer retrieval.
            </p>
          </section>

          {/* TABLE 3: The 4 Intent Stages & Content Mapping Matrix */}
          <div className="my-8">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-cyan-400" />
              <h3 className="font-heading font-bold text-base sm:text-lg text-white">
                Table 3: The 4 Customer Intent Stages &amp; What Content to Build
              </h3>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#090f1e]">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.04]">
                    <th className="p-3.5 font-mono text-cyan-300 font-bold uppercase">Intent Stage</th>
                    <th className="p-3.5 font-mono text-white/80 font-bold uppercase">Customer Mindset</th>
                    <th className="p-3.5 font-mono text-emerald-300 font-bold uppercase">Real Question Example</th>
                    <th className="p-3.5 font-mono text-purple-300 font-bold uppercase">Best Content Format</th>
                    <th className="p-3.5 font-mono text-amber-300 font-bold uppercase">Internal Link Strategy</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06] text-white/80">
                  <tr>
                    <td className="p-3.5 font-bold text-cyan-400">1. Problem Awareness</td>
                    <td className="p-3.5">&quot;Why is this so hard? Is there an easier way?&quot;</td>
                    <td className="p-3.5 text-white/90">&quot;When should a startup stop using Excel for payroll?&quot;</td>
                    <td className="p-3.5 text-emerald-200">How-to guide or problem diagnostic article</td>
                    <td className="p-3.5 text-amber-200">Link down to solution &amp; product comparison pages</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-bold text-cyan-400">2. Solution Research</td>
                    <td className="p-3.5">&quot;What types of tools solve this problem?&quot;</td>
                    <td className="p-3.5 text-white/90">&quot;What features to look for in HRMS software for small teams?&quot;</td>
                    <td className="p-3.5 text-emerald-200">Category guide &amp; feature checklist page</td>
                    <td className="p-3.5 text-amber-200">Link to product tour and pricing calculator</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-bold text-cyan-400">3. Product Comparison</td>
                    <td className="p-3.5">&quot;Which provider is best for my exact situation?&quot;</td>
                    <td className="p-3.5 text-white/90">&quot;Tool A vs Tool B: which is cheaper for 50 users?&quot;</td>
                    <td className="p-3.5 text-emerald-200">Unbiased side-by-side comparison table</td>
                    <td className="p-3.5 text-amber-200">Link to detailed case studies and customer stories</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-bold text-cyan-400">4. Purchase Decision</td>
                    <td className="p-3.5">&quot;Can I trust this? How hard is it to switch?&quot;</td>
                    <td className="p-3.5 text-white/90">&quot;What is the implementation timeline and data migration process?&quot;</td>
                    <td className="p-3.5 text-emerald-200">Security, migration, and implementation breakdown</td>
                    <td className="p-3.5 text-amber-200">Link to demo booking, contact &amp; audit request</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <hr className="border-white/10 my-8" />

          {/* Section 10 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              10. Why Question Memory Is More Valuable Than a Keyword List Alone
            </h2>
            <p>
              Traditional SEO tools are useful. They help you understand search volume, keyword difficulty, competition, and general search demand.
            </p>
            <p>
              But they don&apos;t tell you everything about your customers.
            </p>
            <p>
              Consider the difference:
            </p>

            <div className="overflow-x-auto my-6 rounded-2xl border border-white/10 bg-[#090f1e]">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.04]">
                    <th className="p-4 font-mono text-xs font-bold text-cyan-300 uppercase">
                      Traditional Keyword Research (Tools)
                    </th>
                    <th className="p-4 font-mono text-xs font-bold text-emerald-300 uppercase">
                      Question Memory (Real Intent)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06] text-white/80">
                  <tr>
                    <td className="p-4 font-medium text-white/90">HRMS software</td>
                    <td className="p-4 text-cyan-200">Which HRMS is suitable for a 50-person company?</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-medium text-white/90">Payroll software</td>
                    <td className="p-4 text-cyan-200">Which payroll software handles statutory compliance easily?</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-medium text-white/90">CRM software</td>
                    <td className="p-4 text-cyan-200">Which CRM is best for a small sales team using Gmail?</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-medium text-white/90">Expense management software</td>
                    <td className="p-4 text-cyan-200">How can we reduce manual expense approvals?</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              The first column helps you understand <em>what people search for</em>.
            </p>
            <p>
              The second column helps you understand <em>their problems, requirements and decisions</em>.
            </p>
            <p className="font-bold text-white text-lg">
              You need both.
            </p>
            <p>
              Question Memory does not replace traditional keyword research. It adds the customer context that keyword databases alone cannot provide.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Section 11 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              11. How to Use Question Memory for SEO and AI Search
            </h2>
            <p>
              Once you have identified your most important topics, use them to build your content strategy.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
              <div className="p-5 rounded-2xl bg-[#091122] border border-cyan-500/30 space-y-3">
                <div className="flex items-center gap-2 text-cyan-400">
                  <Search className="w-5 h-5" />
                  <h3 className="font-bold text-white text-base">For Traditional SEO:</h3>
                </div>
                <ul className="list-disc pl-5 space-y-2 text-xs sm:text-sm text-white/80">
                  <li>Validate relevant keywords using search volume and search engine result pages (SERPs).</li>
                  <li>Identify the right search intent (informational, commercial, or transactional).</li>
                  <li>Study competing pages to see what format works best.</li>
                  <li>Create useful articles, detailed product pages, and helpful comparison pages.</li>
                </ul>
              </div>

              <div className="p-5 rounded-2xl bg-[#0d1428] border border-indigo-500/30 space-y-3">
                <div className="flex items-center gap-2 text-indigo-400">
                  <Bot className="w-5 h-5" />
                  <h3 className="font-bold text-white text-base">For AI Search:</h3>
                </div>
                <ul className="list-disc pl-5 space-y-2 text-xs sm:text-sm text-white/80">
                  <li>Understand how customers naturally describe their problems in full sentences.</li>
                  <li>Cover the constraints and requirements behind their questions directly.</li>
                  <li>Provide clear, honest product comparisons and practical explanations.</li>
                  <li>Support claims with real facts, customer stories, and measurable proof.</li>
                  <li>Make information accessible in clean HTML without blocking AI crawlers.</li>
                </ul>
              </div>
            </div>

            <p>
              You can also build a list of important customer prompts and periodically test them across ChatGPT, Claude, Gemini and Perplexity.
            </p>
            <p>
              To monitor whether your brand and website appear in AI search results, you can follow our step-by-step guide on{" "}
              <Link
                to="/blog/how-to-measure-ai-search-visibility-for-free/"
                className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
              >
                how to measure AI search visibility for free without paid tools
              </Link>
              . And once you are ready to turn your Question Memory into published articles, read my 10-principle framework on{" "}
              <Link
                to="/blog/how-to-write-content-that-ranks-on-google-and-improves-ai-visibility/"
                className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
              >
                how to write content that ranks on Google and improves AI visibility
              </Link>
              .
            </p>
            <p>
              Remember: AI prompt research and traditional keyword research provide different signals. Do not assume that every collected question has a measurable search volume in keyword tools. Many high-value buying questions show zero search volume, yet they decide multi-thousand dollar sales.
            </p>
          </section>

          {/* TABLE 4: High-Intent Content Formatting Checklist */}
          <div className="my-8">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <h3 className="font-heading font-bold text-base sm:text-lg text-white">
                Table 4: High-Intent Content Formatting Checklist for AI Search
              </h3>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#090f1e]">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.04]">
                    <th className="p-3.5 font-mono text-cyan-300 font-bold uppercase">Content Element</th>
                    <th className="p-3.5 font-mono text-emerald-300 font-bold uppercase">Why AI Answer Engines Need It</th>
                    <th className="p-3.5 font-mono text-white/80 font-bold uppercase">Traditional SEO Impact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06] text-white/80">
                  <tr>
                    <td className="p-3.5 font-bold text-white">Direct Answer in First 50 Words</td>
                    <td className="p-3.5 text-emerald-200">Provides clear quote extraction for ChatGPT and Google AI Overviews</td>
                    <td className="p-3.5">Increases chances of winning Google Featured Snippets</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-bold text-white">Semantic HTML Tables</td>
                    <td className="p-3.5 text-emerald-200">LLMs parse tabular rows and columns with high factual accuracy</td>
                    <td className="p-3.5">Ranks for comparison &amp; pricing rich result cards</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-bold text-white">Customer Objection FAQs</td>
                    <td className="p-3.5 text-emerald-200">Answers natural follow-up prompts without user leaving the session</td>
                    <td className="p-3.5">Targets long-tail People Also Ask (PAA) queries</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-bold text-white">Original Proof &amp; Case Studies</td>
                    <td className="p-3.5 text-emerald-200">Supplies high-entropy facts that make AI trust your brand as a source</td>
                    <td className="p-3.5">Builds natural backlinks and strengthens Google E-E-A-T</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-bold text-white">Crawlable Internal Links</td>
                    <td className="p-3.5 text-emerald-200">Helps AI bots discover related topical pages during web retrieval</td>
                    <td className="p-3.5">Passes PageRank and establishes topical cluster authority</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <hr className="border-white/10 my-8" />

          {/* Final Thoughts */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              Final Thoughts
            </h2>
            <p>
              Keyword research should not start and end with a list of keywords from Semrush or Ahrefs.
            </p>
            <p>
              Start with the searches that already bring people to your website. Understand the questions they ask across communities, reviews, sales conversations and customer touchpoints.
            </p>
            <p>
              Bring all these questions together in one place.
            </p>
            <p className="font-bold text-cyan-300 text-lg">
              Build your Question Memory.
            </p>
            <p>
              The real opportunity is to identify the recurring problems, requirements and purchase decisions behind those questions.
            </p>
            <p>
              Those are the topics worth covering on your website. For complex technical websites and CMS platforms, this approach has helped drive record organic pipelines—as seen in our{" "}
              <Link
                to="/casestudy/seismic/"
                className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
              >
                Seismic enterprise SEO case study
              </Link>
              .
            </p>
            <blockquote className="p-5 sm:p-6 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-bold text-lg sm:text-xl shadow-xl my-6 not-italic">
              Don&apos;t just research what people search for. Understand what they are trying to solve.
            </blockquote>
          </section>

        </article>

        {/* Interactive Question Memory Tracker (Action Checklist) */}
        <section className="my-10 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-[#0c1428] via-[#090e1c] to-[#070b16] border border-cyan-500/30 shadow-2xl relative overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-6 h-6 text-cyan-400" />
              <div>
                <h3 className="font-heading font-bold text-xl sm:text-2xl text-white">
                  Action Checklist: Build Your Question Memory
                </h3>
                <p className="text-xs text-white/60 mt-0.5">
                  Check off each step as you gather and map customer intent questions for your business:
                </p>
              </div>
            </div>
            <span className="text-xs font-mono text-cyan-300 bg-cyan-950/70 border border-cyan-500/30 px-3 py-1.5 rounded-full w-fit shrink-0">
              {completedCount} of {checklistItems.length} completed
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {checklistItems.map((item) => {
              const isChecked = !!checkedSteps[item.id];
              return (
                <button
                  key={item.id}
                  onClick={() => toggleStep(item.id)}
                  className={`text-left p-3.5 rounded-xl border transition-all flex items-start gap-3 cursor-pointer ${
                    isChecked
                      ? "bg-cyan-950/40 border-cyan-500/50 text-white shadow-lg shadow-cyan-950/20"
                      : "bg-[#090f1d] border-white/10 hover:border-white/20 text-white/80"
                  }`}
                >
                  <span className="mt-0.5 shrink-0 text-cyan-400">
                    {isChecked ? <CheckSquare className="w-4 h-4 text-emerald-400" /> : <Square className="w-4 h-4 text-white/40" />}
                  </span>
                  <div>
                    <h4 className="text-xs font-bold font-mono text-white mb-0.5">{item.title}</h4>
                    <p className="text-[11px] text-white/60 leading-snug">{item.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-xs sm:text-sm text-white/75">
              Need a done-for-you AI Visibility Audit with tailored customer fan-out topics and technical fixes?
            </p>
            <Link
              to="/ai-visibility-audit/"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-400/40 text-cyan-300 hover:text-white text-xs font-mono font-bold uppercase tracking-wider transition-all"
            >
              <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
              <span>Book Your AI Visibility Audit</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </section>

        {/* Related Deep Dives & Case Studies */}
        <section className="my-12 space-y-6">
          <div className="flex items-center justify-between pb-3 border-b border-white/10">
            <h3 className="font-heading font-bold text-xl text-white tracking-tight flex items-center gap-2">
              <Layers className="w-5 h-5 text-cyan-400" />
              <span>Related Technical Playbooks &amp; Case Studies</span>
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Link
              to="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-cyan-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 uppercase">
                  AEO / GEO &amp; AI Visibility
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-cyan-400 transition-colors leading-snug">
                  Why Do I Rank on Google but Don&apos;t Appear in ChatGPT?
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Discover why competitors appear in ChatGPT while you don&apos;t, how fan-out queries work, and how to fix crawler blocks.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-cyan-400 font-semibold">
                <span>Read Playbook</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/how-to-audit-a-website-for-ai-visibility/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-indigo-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 uppercase">
                  10-Point Technical Checklist
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-indigo-400 transition-colors leading-snug">
                  How Do I Audit My Website for Better AI Visibility?
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Audit your website yourself across crawler access, JavaScript rendering parity, fan-out questions, and citations.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-indigo-400 font-semibold">
                <span>Read Audit Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/blog/how-to-measure-ai-search-visibility-for-free/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-emerald-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 uppercase">
                  Free Analytics Framework
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-emerald-400 transition-colors leading-snug">
                  How to Measure AI Search Visibility for Free
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Step-by-step 5-source framework using GSC, Bing Webmaster Tools, Server Logs, GA4, and Clarity without paid tools.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-emerald-400 font-semibold">
                <span>Read Free Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </section>

        {/* Author Bio Card */}
        <footer className="mt-12 pt-8 border-t border-white/10">
          <div className="p-6 sm:p-7 rounded-3xl bg-[#0a0f1d] border border-white/10 flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left shadow-2xl">
            <img
              src="/narendra-workstation.jpg"
              alt="Kanna Narendra Kumar"
              className="w-24 h-24 rounded-2xl object-cover object-[center_20%] border border-cyan-500/40 shrink-0 shadow-xl"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/narendra-avatar.jpg";
              }}
            />
            <div className="space-y-2.5 flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h4 className="font-heading font-bold text-lg text-white">
                    Written by Kanna Narendra Kumar
                  </h4>
                  <p className="text-xs font-mono text-cyan-400">
                    Senior SEO Specialist &amp; AI Search Strategist
                  </p>
                </div>
                <a
                  href="https://www.linkedin.com/in/narendra-kumar-kanna/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-xs text-white/80 font-mono transition-colors self-center sm:self-auto"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>LinkedIn Profile</span>
                </a>
              </div>
              <p className="text-xs sm:text-sm text-white/75 leading-relaxed font-sans">
                Senior SEO Specialist &amp; AI Search Strategist with 8+ years scaling organic visibility and high-intent inbound pipeline for global B2B SaaS. Pioneer of the Hybrid Engine Optimization (HEO) framework unifying traditional search with generative AI platforms. Available globally for strategic advisory and high-impact enterprise execution.
              </p>
            </div>
          </div>
        </footer>

        {/* Collaboration CTA Footer */}
        <section className="mt-10 p-8 sm:p-10 rounded-3xl bg-[#0c1222] border border-white/10 text-center space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 border border-cyan-500/30 flex items-center justify-center mx-auto text-cyan-400">
            <Zap className="w-6 h-6" />
          </div>
          <h3 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight uppercase">
            Want to Turn Customer Questions Into Revenue?
          </h3>
          <p className="text-sm text-white/60 max-w-xl mx-auto leading-relaxed">
            Whether you want to build your Question Memory, rank in ChatGPT and Google AI Overviews, or optimize your B2B SaaS inbound pipeline, let&apos;s connect directly.
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/ai-visibility-audit/"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-transform hover:-translate-y-0.5 cursor-pointer"
            >
              <Sparkles className="w-4 h-4" />
              <span>Get an AI Visibility Audit</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/[0.05] hover:bg-white/[0.1] border border-cyan-500/30 text-white text-xs font-bold uppercase tracking-wider transition-colors"
            >
              <span>Schedule Strategy Discussion</span>
              <ArrowUpRight className="w-4 h-4 text-cyan-400" />
            </Link>
          </div>
        </section>

      </main>
    </div>
  );
}
